import { useEffect, useState } from 'react';
import { Download, Loader2, ShieldCheck } from 'lucide-react';
import { api } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import { etiquetaCampo } from '../experiencia/etiquetas';
import { etiquetaRol } from '../lib/roles';

/* =====================================================================
   Mis datos · CUMPLIMIENTO F5
   =====================================================================
   Lo que TEGO guarda de la persona que mira, qué aviso aceptó, y sus
   solicitudes sobre sus datos. Se baja en un archivo (derecho de acceso).
   Aceptar la versión vigente del aviso también se hace aquí cuando la
   pantalla dice que falta.
   ===================================================================== */

export default function MisDatos() {
  const avisos = useAvisos();
  const [d, setD] = useState(null);
  const [pide, setPide] = useState([]);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/cumplimiento/mis-datos'), api.get('/legal/vigentes/cuenta_personal').catch(() => ({ vigentes: [] }))])
      .then(([datos, v]) => { if (vivo) { setD(datos); setPide(v.vigentes || []); setError(''); } })
      .catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  if (error && !d) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!d) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Reuniendo lo que TEGO guarda de ti…
      </p>
    );
  }
  const aceptadas = new Set((d.aceptaciones || []).map((a) => `${a.clave}:${a.version}`));
  const faltan = pide.filter((v) => !aceptadas.has(`${v.clave}:${v.version}`));

  async function aceptar() {
    try {
      const cuerpo = {};
      pide.forEach((v) => { cuerpo[v.clave] = v.version; });
      const r = await api.post('/cumplimiento/aceptar', { aceptadas: cuerpo });
      avisos.exito(r.mensaje);
      setTick((t) => t + 1);
    } catch (e) { avisos.error(e.message); }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Mis datos</h1>
        <p className="text-gray-600">Lo que TEGO guarda de ti como persona, y lo que puedes pedir.</p>
        <Ayuda id="cumplimiento.mis_datos.pantalla" />
      </header>

      {faltan.length > 0 && (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="font-medium text-amber-900">Hay una versión nueva del aviso pendiente de aceptar.</p>
          <ul className="mt-1 text-sm text-amber-900">
            {faltan.map((v) => <li key={v.clave}>«{v.clave}» versión {v.version}, vigente desde {v.vigente_desde} — <a className="underline" href={`/api/legal/${v.clave}`} target="_blank" rel="noreferrer">leerlo</a></li>)}
          </ul>
          <button type="button" onClick={aceptar}
                  className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Lo leí y lo acepto
          </button>
        </section>
      )}

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Tu ficha</h2>
        <dl className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
          {Object.entries(d.personales || {}).map(([k, v]) => (
            <div key={k} className="flex gap-2"><dt className="w-32 text-gray-500">{etiquetaCampo(k)}</dt><dd className="text-gray-900">{v === null || v === undefined ? '—' : String(v)}</dd></div>
          ))}
          <div className="flex gap-2"><dt className="w-32 text-gray-500">Rol</dt><dd>{etiquetaRol(d.rol)}</dd></div>
          <div className="flex gap-2"><dt className="w-32 text-gray-500">En TEGO desde</dt><dd>{String(d.en_tego_desde || '').slice(0, 10)}</dd></div>
        </dl>
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Lo que hiciste</h2>
        <p className="text-sm text-gray-600">{d.dice}</p>
        <Ayuda id="cumplimiento.mis_datos.hechos" />
        <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
          {Object.entries(d.cuentas || {}).map(([k, v]) => <li key={k} className="text-gray-700">{etiquetaCampo(k)}: <strong>{v}</strong></li>)}
        </ul>
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">El aviso que aceptaste</h2>
        {(d.aceptaciones || []).length === 0
          ? <p className="text-sm text-gray-600">Sin versión vigente aceptada: no hay ninguna publicada o falta aceptarla.</p>
          : <ul className="mt-1 text-sm text-gray-700">{d.aceptaciones.map((a, i) => <li key={i}>«{a.clave}» versión {a.version} · {String(a.aceptado_en).slice(0, 16).replace('T', ' ')} · desde {a.punto.replace('_', ' ')}</li>)}</ul>}
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Tus solicitudes sobre tus datos</h2>
        {(d.solicitudes_arco || []).length === 0
          ? <p className="text-sm text-gray-600">Ninguna. Puedes pedir acceso, rectificación, cancelación u oposición desde «Mi cuenta».</p>
          : <ul className="mt-1 text-sm text-gray-700">{d.solicitudes_arco.map((s) => <li key={s.id}>{s.tipo} · {s.estado} · responder antes de {s.responder_antes_de}</li>)}</ul>}
      </section>

      <button type="button" onClick={async () => { try { guardarArchivo(await api.bajar('/cumplimiento/mis-datos.json'), 'mis-datos-tego.json'); } catch (e) { avisos.error(e.message); } }}
              className="inline-flex w-fit items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 font-semibold text-gray-800">
        <Download className="h-4 w-4" aria-hidden="true" /> Bajar mis datos en un archivo
      </button>
    </div>
  );
}
